import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { fetchExperiences } from "store/experiences"
import { clearedCalendar, getAvailabilityCalendar } from "store/availability"
import { DATE_KEY_FORMAT } from "../constants"
import { buildMonthGrid } from "../utils"

/**
 * Owns the availability page's state: which listing, which month, and the day feed for the two.
 *
 * The listing is required before anything is fetched (A1) — the page shows its empty state until
 * one is picked, which is the design's first frame.
 */
export const useAvailabilityCalendar = () => {
  const dispatch = useDispatch()

  const [listingId, setListingId] = useState(null)
  const [monthDate, setMonthDate] = useState(() => moment().startOf("month"))
  /** The day the details panel is showing, "YYYY-MM-DD", or null when it is closed. */
  const [selectedDate, setSelectedDate] = useState(null)

  const listings = useSelector((state) => state.experiences?.list || [])
  const isLoadingListings = useSelector((state) => Boolean(state.experiences?.loading))
  const days = useSelector((state) => state.availability?.calendar?.days || {})
  const listing = useSelector((state) => state.availability?.calendar?.listing || null)
  const isLoading = useSelector((state) => Boolean(state.availability?.calendar?.loading))
  const hasFailed = useSelector((state) => Boolean(state.availability?.calendar?.failed))

  const { weeks, start, end } = useMemo(() => buildMonthGrid(monthDate), [monthDate])

  useEffect(() => {
    dispatch(fetchExperiences())
  }, [dispatch])

  const refetch = useCallback(() => {
    if (!listingId) return
    // `api()` rejects on failure and the reducer's `failed` flag is what the grid reads.
    dispatch(getAvailabilityCalendar({ listing_id: listingId, start_date: start, end_date: end })).catch(() => {})
  }, [dispatch, listingId, start, end])

  useEffect(() => {
    if (!listingId) {
      dispatch(clearedCalendar())
      return
    }
    refetch()
  }, [dispatch, listingId, refetch])

  // A listing change makes the open day meaningless — its state belonged to the previous listing.
  useEffect(() => setSelectedDate(null), [listingId])

  const listingOptions = useMemo(
    () => listings.map((experience) => ({ value: String(experience.id), label: experience.title || experience.name })),
    [listings]
  )

  const stepMonth = useCallback((offset) => setMonthDate((current) => current.clone().add(offset, "month")), [])

  /**
   * Stepping the panel past the edge of the visible grid takes the grid with it, so the day the
   * panel names is always a day the feed has been asked about.
   */
  const goToDate = useCallback(
    (dateKey) => {
      setSelectedDate(dateKey)
      const target = moment(dateKey, DATE_KEY_FORMAT)
      if (!target.isValid()) return
      setMonthDate((current) => (target.isSame(current, "month") ? current : target.clone().startOf("month")))
    },
    []
  )

  return {
    listingId,
    setListingId,
    listingOptions,
    isLoadingListings,
    monthDate,
    stepMonth,
    selectedDate,
    goToDate,
    closeDetails: () => setSelectedDate(null),
    refetch,
    weeks,
    days,
    /** The listing as the availability feed describes it — carries `listing_type` for ticketed. */
    listing,
    isLoading,
    hasFailed
  }
}

export default useAvailabilityCalendar
